import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { CgSpinner } from "react-icons/cg";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import PrintQr from "../components/PrintQr";
import "./print.css";

const GenerateQrPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(""); // Store only the batch ID
  const [batchDisplay, setBatchDisplay] = useState(""); // Store the display name
  const [numEntries, setNumEntries] = useState("");
  const [qrType, setQrType] = useState("primary");
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();
  const [qrList, setQrList] = useState([]);
  const [receivedValue, setReceivedValue] = useState(false);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userData, setUserData] = useState({
    name: "",
    role: "",
    lastLogin: "",
    companyName: "",
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get("/protected");
        const { name, role, last_login, company_name } = response.data;
        setUserData({
          name,
          role,
          lastLogin: last_login,
          companyName: company_name,
        });
        if (role === "salesman") {
          navigate("/dashboard");
        }

        const batchesResponse = await axios.get("/batches");
        setBatches(batchesResponse.data);
        setFilteredBatches(batchesResponse.data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        navigate("/login");
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleBatchSearch = (e) => {
    const query = e.target.value;
    setBatchDisplay(query);
    setShowDropdown(true);

    if (query) {
      const filtered = batches.filter((batch) =>
        batch.batch_number.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredBatches(filtered);
    } else {
      setFilteredBatches(batches);
    }
  };

  const handleBatchSelect = (batch) => {
    setSelectedBatch(batch.id); // Set only the batch ID
    setBatchDisplay(
      `${batch.batch_number} - ${batch.product_alias?.product_alias}`
    );
    setShowDropdown(false);
  };

  const handleGenerateQr = async () => {
    try {
      const endpoint =
        qrType === "primary" ? "/generate-qr" : "/generate-secondary-qr";
      const response = await axios.post(
        endpoint,
        {
          batch_id: selectedBatch, // Only the batch ID is sent
          num_entries: parseInt(numEntries, 10),
        },
        {
          responseType: "blob",
        }
      );
      console.log(response.data.message)
      if (response.data.message === "QR already generated") {
        setShowDialog(true);
      } else {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "encrypted.xlsx");

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data.message === "QR already generated"
      ) {
        setShowDialog(true); // Show the dialog for "QR already generated"
      } else {
        console.error("Error generating QR codes:", error);
      }
    }
  };

  const handleReprintQr = async () => {
    try {
      const endpoint =
        qrType === "primary" ? "/reprint-qr" : "/reprint-secondary-qr";
      const response = await axios.post(endpoint, {
        batch_id: selectedBatch, // Only the batch ID is sent
      });

      setQrList(response.data.qr_data);
      setShowDialog(false);
      console.log("QR Codes:", response.data.qr_data);
    } catch (error) {
      console.error("Error reprinting QR codes:", error);
    }
  };

  const handleRegenerateQr = async () => {
    try {
      const endpoint =
        qrType === "primary" ? "/regenerate-qr" : "/regenerate-secondary-qr";
      const response = await axios.post(
        endpoint,
        {
          batch_id: selectedBatch, // Only the batch ID is sent
        },
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "encrypted.xlsx");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setShowDialog(false);
    } catch (error) {
      console.error("Error regenerating QR codes:", error);
    }
  };

  const handlePrintQr = async () => {
    try {
      const endpoint =
        qrType === "primary" ? "/print-qr" : "/print-secondary-qr";
      const response = await axios.post(endpoint, {
        batch_id: selectedBatch, // Only the batch ID is sent
        num_entries: parseInt(numEntries, 10),
      });

      setQrList(response.data.qr_data);
      console.log("QR Codes:", response.data.qr_data);
    } catch (error) {
      console.log(error.response)
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data.message === "QR already generated"
      ) {
        setShowDialog(true); // Show the dialog for "QR already generated"
      } else {
        console.error("Error generating QR codes:", error);
      }
    }
  };

  const handleValueFromChild = (value) => {
    setReceivedValue(value);
  };

  return (
    <div>
      {isLoading ? (
        <div className="flex justify-center items-center w-full h-full bg-gray-100 min-h-screen">
          <CgSpinner className="animate-spin text-4xl" />
        </div>
      ) : (
        <>
          <div className="no-print flex bg-gray-100 min-h-screen">
            <Sidebar userData={userData} />
            <div className="flex-1">
              <Header userData={userData} />
              <div className="p-6">
                <h1>Generate QR</h1>
                <div className="flex justify-center bg-gray-100">
                  <div className="w-full max-w-xs bg-white p-6 shadow-md rounded-lg">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Select Batch
                    </label>
                    <div className="relative w-full">
                      <input
                        type="text"
                        value={batchDisplay} // Display name for the input
                        onChange={handleBatchSearch}
                        onFocus={() => setShowDropdown(true)}
                        onBlur={() =>
                          setTimeout(() => setShowDropdown(false), 400)
                        }
                        className="block w-full p-2 border border-gray-300 rounded"
                        placeholder="Type to search batch"
                      />
                      {showDropdown && filteredBatches.length > 0 && (
                        <div className="absolute left-0 right-0 bg-white border border-gray-300 mt-1 rounded max-h-40 overflow-y-auto shadow-lg">
                          {filteredBatches.map((batch) => (
                            <div
                              key={batch.id}
                              className="p-2 hover:bg-gray-200 cursor-pointer"
                              onClick={() => handleBatchSelect(batch)}
                            >
                              {batch.batch_number} -{" "}
                              {batch.product_alias?.product_alias}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Number of QR Codes
                      </label>
                      <input
                        type="number"
                        value={numEntries}
                        onChange={(e) => setNumEntries(e.target.value)}
                        className="block w-full p-2 border border-gray-300 rounded"
                      />
                    </div>

                    {/* Radio buttons for QR type */}
                    <div className="mt-4">
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        QR Type
                      </label>
                      <div className="flex items-center mb-2">
                        <input
                          type="radio"
                          id="primaryQR"
                          name="qrType"
                          value="primary"
                          checked={qrType === "primary"}
                          onChange={() => setQrType("primary")}
                          className="mr-2"
                        />
                        <label
                          htmlFor="primaryQR"
                          className="text-sm text-gray-700"
                        >
                          Primary QR
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="boxQR"
                          name="qrType"
                          value="box"
                          checked={qrType === "box"}
                          onChange={() => setQrType("box")}
                          className="mr-2"
                        />
                        <label
                          htmlFor="boxQR"
                          className="text-sm text-gray-700"
                        >
                          Box QR
                        </label>
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        onClick={handleGenerateQr}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 w-full"
                      >
                        Generate and Download QR
                      </button>
                    </div>
                    <div className="mt-6">
                      <button
                        onClick={handlePrintQr}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-full"
                      >
                        Print QR
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dialog Box for "QR already generated" */}
          {showDialog && (
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center">
              <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full text-center">
                <p className="text-lg mb-4">
                  QR already generated for given Batch Number
                </p>
                <div className="flex justify-around mt-4">
                  <button
                    onClick={handleRegenerateQr}
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                  >
                    Regenerate QR
                  </button>
                  <button
                    onClick={handleReprintQr}
                    className="bg-green-600 text-white px-4 py-2 rounded"
                  >
                    Reprint QR
                  </button>
                  <button
                    onClick={() => setShowDialog(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PrintQr component */}
          <div className="print-only">
            <PrintQr qrList={qrList} sendDataToParent={handleValueFromChild} />
          </div>
        </>
      )}
    </div>
  );
};

export default GenerateQrPage;
